import React, { useCallback, useEffect, useState } from "react";

import moment from "moment";

import {
  addCreditPurchase,
  deleteCreditPurchase,
  getPartnerCreditAssignments,
  getPartnerCreditPurchases,
  unassignCredit,
  updateAssignedCredit,
} from "services";

import { getShapefileRegions, incomplete, useCancelAsyncOnUnmount, useNavigate } from "utils";

import {
  AdminSection,
  AdminTable,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  Form,
  InputNumber,
  Option,
  PageSpin,
  Radio,
  Row,
  Select,
  TextArea,
  Typography,
  Message as message,
} from "components";

export const PartnerCredits = ({ partner, onUpdateSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [updateCreditLoading, setUpdateCreditLoading] = useState(false);
  const [creditAssignments, setCreditAssignments] = useState([]);
  const [creditPurchases, setCreditPurchases] = useState([]);
  const [showUpdateAssignedCreditDrawer, setShowUpdateAssignedCreditDrawer] = useState(false);
  const [selectedAssignedCredit, setSelectedAssignedCredit] = useState(null);
  const [regionEnums, setRegionEnums] = useState(true);

  const signal = useCancelAsyncOnUnmount()();
  const navigate = useNavigate();

  const loadFieldShapeRegions = useCallback(() => {
    getShapefileRegions({ signal }).then((regionEnums) => {
      setRegionEnums(regionEnums);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getCreditAssignments = useCallback(() => {
    setLoading(true);
    getPartnerCreditAssignments({
      id: partner.id,
      signal,
    })
      .then((res) => {
        setCreditAssignments(res);
      })
      .finally(() => setLoading(false));
  }, [partner, signal]);

  const getCreditPurchases = useCallback(() => {
    setLoading(true);
    getPartnerCreditPurchases({
      id: partner.id,
      signal,
    })
      .then((res) => {
        setCreditPurchases(res);
      })
      .finally(() => setLoading(false));
  }, [partner, signal]);

  const complete = (v) => {
    setLoading(true);
    addCreditPurchase({
      ...v,
      signal,
      partnerId: partner.id,
    })
      .then(() => {
        message({
          content: "The gremlins completed your request.",
          icon: "gremlin",
        });
        onUpdateSuccess();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCreditUnassignment = (assignmentId) => {
    unassignCredit({
      partnerId: partner.id,
      assignmentId,
      signal,
    })
      .then(() => {
        getCreditAssignments();
        onUpdateSuccess();
        message({
          content: "The gremlins completed your request.",
          icon: "gremlin",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const closeCreditUpdateDrawer = () => {
    setSelectedAssignedCredit(null);
    setShowUpdateAssignedCreditDrawer(false);
  };

  const handleCreditUpdate = (v) => {
    setUpdateCreditLoading(true);
    if (v.quantity !== selectedAssignedCredit.credits) {
      if (v.quantity > selectedAssignedCredit.credits) {
        message({
          content: "Credit assignments can only be reduced.",
        });
        setUpdateCreditLoading(false);
        return;
      }
      if (selectedAssignedCredit.credits < 0) {
        message({
          content: "Negative credit assignments can not be updated.",
        });
        setUpdateCreditLoading(false);
        return;
      }
      if (v.quantity <= 0) {
        message({
          content: "You can not update credit assignments to 0 or lower",
        });
        setUpdateCreditLoading(false);
        return;
      }
    }
    updateAssignedCredit({
      assignmentId: selectedAssignedCredit.assignmentId,
      program: selectedAssignedCredit.program,
      credits: v.quantity,
      category1: v.category1,
      category2: v.category2,
      category3: v.category3,
      category4: v.category4,
      creditType: v.creditType,
      signal,
    }).then(() => {
      message({
        content: "Credit assignment was successfully updated.",
      });
      setUpdateCreditLoading(false);
      closeCreditUpdateDrawer();
      getCreditAssignments();
    });
  };

  const openCreditUpdateDrawer = (credit) => {
    setSelectedAssignedCredit(credit);
    setShowUpdateAssignedCreditDrawer(true);
  };

  const handleDeleteCreditPurchase = (purchase) => {
    deleteCreditPurchase({
      purchaseId: purchase.id,
      signal,
    })
      .then(() => {
        getCreditAssignments();
        onUpdateSuccess();
        message({
          content: "The gremlins completed your request.",
          icon: "gremlin",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadFieldShapeRegions();
    getCreditAssignments();
    getCreditPurchases();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const moduleAssignments = creditAssignments?.filter(({ program }) => program === "Soil+");
  const methodAssignments = creditAssignments?.filter(({ program }) => program === "MP");

  return loading ? (
    <PageSpin />
  ) : (
    <>
      <Row>
        <Col>
          <Form className="midi-form" name="confirm" onFinish={(v) => complete(v)} onFinishFailed={(v) => incomplete(v)}>
            <AdminSection headline="Add credit purchase" />
            <Row>
              <Col sm={24} md={12}>
                <InputNumber name="quantity" label="Quantity" rules={[{ required: true }]} />
              </Col>
              <Col sm={24} md={12}>
                <InputNumber name="price" label="Price per-credit" rules={[{ required: true }]} />
              </Col>
              <Col sm={24} md={12}>
                <DatePicker name="purchasedAt" label="Purchase date" rules={[{ required: true }]} format={["DD.MM.YYYY"]} showNow={false} />
              </Col>
              <Col sm={24} md={12}>
                <div className="margin-bottom-75 grey-text paragraph-s">Type</div>
                <Form.Item name="future" rules={[{ required: true }]} className="no-suffix" initialValue={false}>
                  <Radio.Group>
                    <Radio value={false}>Past</Radio>
                    <Radio value={true}>Future</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col>
                <TextArea name="notes" label="Notes" rules={[{ required: true }]} rows={2} />
              </Col>
            </Row>

            <Row>
              <Col sm={24} md={12}>
                <Select name="creditType" label="Credit Type" rules={[{ required: true }]}>
                  <Option value={"Insetting"}>Insetting</Option>
                  <Option value={"Offsetting"}>Offsetting</Option>
                </Select>
              </Col>
              <Col sm={24} md={12}>
                <Form.Item name="prePurchase" valuePropName="checked">
                  <Checkbox>pre-purchase</Checkbox>
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col sm={24} md={12}>
                <DatePicker name="deliveryPeriodStart" label="Delivery Period Start" rules={[{ required: true }]} format={["DD.MM.YYYY"]} showNow={false} />
              </Col>
              <Col sm={24} md={12}>
                <DatePicker name="deliveryPeriodEnd" label="Delivery Period End " rules={[{ required: true }]} format={["DD.MM.YYYY"]} showNow={false} />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextArea name="description" label="Description" rules={[{ required: true }]} rows={2} />
              </Col>
            </Row>

            <Row>
              <Col sm={12} md={12}>
                <TextArea name="attachments" label="Attachments" rules={[{ required: true }]} rows={1} />
              </Col>

              <Col sm={12} md={12}>
                <TextArea name="dealId" label="Deal Id" rules={[{ required: true }]} rows={1} />
              </Col>
            </Row>

            <Row>
              <Col sm={24} md={12}>
                <InputNumber name="productsWithKlimLabel" label="Products With Klim Label" rules={[{ required: true }]} />
              </Col>
              <Col sm={24} md={12}>
                <InputNumber name="desiredVintage" label="Desired Vintage" rules={[{ required: true }]} />
              </Col>
            </Row>

            <Row>
              <Col sm={24} md={12}>
                <Select name="desiredRegions" label="Desired Regions" mode="tags" rules={[{ required: true }]}>
                  {Object.keys(regionEnums).map((item, i) => (
                    <Option value={regionEnums[item]} key={i}>
                      {regionEnums[item]}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col sm={24} md={12}>
                <Select name="desiredProgram" label="Desired Program" rules={[{ required: true }]}>
                  <Option value={"Method"}>Method</Option>
                  <Option value={"Soil+"}>Soil+</Option>
                </Select>
              </Col>
            </Row>

            <Row>
              <Col sm={24} md={12}>
                <TextArea name="issuanceRegistryId" label="Issuance Registry Id" rules={[{ required: true }]} rows={1} />
              </Col>
              <Col sm={24} md={12}>
                <DatePicker name="retirementDate" label="Retirement Date" rules={[{ required: true }]} format={["DD.MM.YYYY"]} showNow={false} />
              </Col>
            </Row>

            <Row>
              <Col>
                <TextArea name="retirementReason" label="Retirement Reason" rules={[{ required: true }]} rows={1} />
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Item>
                  <Button type="primary" htmlType="submit" disabled={loading} loading={loading}>
                    Add
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      <AdminSection headline="Credit purchases" />
      <Row className="midi-form" style={{ textAlign: "center" }}>
        <Col sm={8} md={8}>
          <Card className="content-card" style={{ margin: "0 0 3rem" }}>
            <div style={{ margin: "0 auto" }}>
              <Typography variant="h3">{partner.purchasedCredits?.toFixed(3)}</Typography>
              <Typography variant="paragraph-m">Total purchased credits</Typography>
            </div>
          </Card>
        </Col>
        <Col sm={8} md={8}>
          <Card className="content-card" style={{ margin: "0 0 3rem" }}>
            <div style={{ margin: "0 auto" }}>
              <Typography variant="h3">{partner.pendingCredits?.toFixed(3)}</Typography>
              <Typography variant="paragraph-m">Total pending credits</Typography>
            </div>
          </Card>
        </Col>
      </Row>

      <AdminSection headline="Assigned Credits" />
      <Row className="midi-form" style={{ textAlign: "center" }}>
        <Col sm={8} md={8}>
          <Card className="content-card" style={{ margin: "0 0 3rem" }}>
            <div style={{ margin: "0 auto" }}>
              <Typography variant="h3">{partner.receivedMethodCredits?.toFixed(3)}</Typography>
              <Typography variant="paragraph-m">Received Method Credits</Typography>
            </div>
          </Card>
        </Col>
        <Col sm={8} md={8}>
          <Card className="content-card" style={{ margin: "0 0 3rem" }}>
            <div style={{ margin: "0 auto" }}>
              <Typography variant="h3">{partner.totalRemovals?.toFixed(3)}</Typography>
              <Typography variant="paragraph-m">Total Removals</Typography>
            </div>
          </Card>
        </Col>
        <Col sm={8} md={8}>
          <Card className="content-card" style={{ margin: "0 0 3rem" }}>
            <div style={{ margin: "0 auto" }}>
              <Typography variant="h3">{partner.totalReductions?.toFixed(3)}</Typography>
              <Typography variant="paragraph-m">Total Reductions</Typography>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          {creditPurchases.length > 0 && (
            <AdminTable
              dataSource={creditPurchases.sort((a, b) => moment(b.purchasedAt).valueOf() - moment(a.purchasedAt).valueOf())}
              config={{
                actions: [
                  {
                    icon: "tabler/trash",
                    route: "/",
                    func: handleDeleteCreditPurchase,
                    action: "creditAssign",
                    tooltip: "Delete this purchase",
                  },
                  {
                    icon: "tabler/edit",
                    route: "/",
                    func: (purchase) => navigate(`/partners/credit-purchases/${purchase.id}/update`),
                    action: "creditAssign",
                    tooltip: "Edit this purchase",
                  },
                ],
                exclude: [],
              }}
            />
          )}
        </Col>
      </Row>

      <AdminSection headline="Assigned Module credits" />
      <Row>
        <Col>
          {moduleAssignments.length > 0 && (
            <AdminTable
              dataSource={moduleAssignments
                ?.map(({ farmFieldModuleId, id, ...rest }) => ({ assignmentId: id, fieldModuleId: farmFieldModuleId, ...rest }))
                .sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())}
              config={{
                actions: [
                  {
                    icon: "tabler/trash",
                    arguments: ["assignmentId"],
                    func: handleCreditUnassignment,
                    tooltip: "Delete this assignment",
                  },
                  {
                    icon: "tabler/edit",
                    func: openCreditUpdateDrawer,
                    tooltip: "Edit this assignment",
                  },
                ],
                exclude: ["farmFieldMethodId", "credits", "program", "createdAt"],
              }}
            />
          )}
        </Col>
      </Row>
      <AdminSection headline="Assigned Method credits" />
      <Row>
        <Col>
          {methodAssignments.length > 0 && (
            <AdminTable
              dataSource={methodAssignments.sort((a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf())}
              config={{
                actions: [
                  {
                    icon: "tabler/trash",
                    arguments: ["id"],
                    func: handleCreditUnassignment,
                    tooltip: "Delete this assignment",
                  },
                  {
                    icon: "tabler/edit",
                    func: openCreditUpdateDrawer,
                    tooltip: "Edit this assignment",
                  },
                ],
                exclude: ["farmFieldMethodId", "farmFieldModuleId", "creditsCategory1", "creditsCategory2", "creditsCategory3", "creditsCategory4", "program"],
              }}
            />
          )}
        </Col>
      </Row>
      <Drawer title="Update credit" visible={showUpdateAssignedCreditDrawer} onClose={() => closeCreditUpdateDrawer()}>
        {selectedAssignedCredit && (
          <>
            <Form name="creditAssignment" onFinish={handleCreditUpdate} onFinishFailed={incomplete}>
              <Row className="margin-top-100">
                <Col sm={24} md={24}>
                  <Select initialValue={selectedAssignedCredit.creditType} name="creditType" label="Credit Type" rules={[{ required: true }]}>
                    <Option value={"Insetting"}>Insetting</Option>
                    <Option value={"Offsetting"}>Offsetting</Option>
                  </Select>
                </Col>
              </Row>
              {selectedAssignedCredit.program === "MP" && (
                <Row>
                  <Col sm={24} md={24}>
                    <InputNumber
                      label="Quantity"
                      name="quantity"
                      rules={[{ required: true }]}
                      message={"This is required"}
                      initialValue={selectedAssignedCredit.credits}
                    />
                  </Col>
                </Row>
              )}
              {selectedAssignedCredit.program === "Soil+" && (
                <Row>
                  <Col sm={24} md={24}>
                    <InputNumber
                      label="Quantity category1"
                      name="category1"
                      rules={[{ required: true }]}
                      message={"This is required"}
                      disabled
                      initialValue={selectedAssignedCredit.category1}
                    />
                  </Col>
                  <Col sm={24} md={24}>
                    <InputNumber
                      label="Quantity category2"
                      name="category2"
                      rules={[{ required: true }]}
                      message={"This is required"}
                      disabled
                      initialValue={selectedAssignedCredit.category2}
                    />
                  </Col>
                  <Col sm={24} md={24}>
                    <InputNumber
                      label="Quantity category3"
                      name="category3"
                      rules={[{ required: true }]}
                      message={"This is required"}
                      disabled
                      initialValue={selectedAssignedCredit.category3}
                    />
                  </Col>
                  <Col sm={24} md={24}>
                    <InputNumber
                      label="Quantity category4"
                      name="category4"
                      rules={[{ required: true }]}
                      message={"This is required"}
                      disabled
                      initialValue={selectedAssignedCredit.category4}
                    />
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  <Button block type="primary" htmlType="submit" loading={updateCreditLoading}>
                    Update assigned credit type
                  </Button>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Drawer>
    </>
  );
};
